import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GetAllStudentJobs } from '@edtech-ui/core/models/student-job-management/get-all-student-jobs';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { ToastrService } from 'ngx-toastr';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap, switchMap } from 'rxjs';

@Component({
	selector: 'app-jobs-dashboard',
	standalone: true,
	imports: [CommonModule, RouterModule, MatProgressSpinnerModule, MatTooltipModule],
	templateUrl: './jobs-dashboard.component.html',
	styleUrl: './jobs-dashboard.component.scss'
})
export class JobsDashboardComponent implements OnInit, OnDestroy {


	jobData: GetAllStudentJobs[] = [];
	subscription = new Subscription;
	total: number = 0;
	allJobsCount: number = 0;
	totalPages: number = 0;
	pages: number = 0;
	limit: number = 10;
	recommendedCount: number = 0;
	myApplicationsCount: number = 0;
	savedCount: number = 0;
	isLoading: boolean = false;
	searchTerm = '';
	selectedWorkMode = '';
	selectedStatus = '';
	currentPage = 1;
	searchSubject = new Subject<string>();
	selectedJobType = ''
	location = '';
	locationSubject = new Subject<string>();
	selectedTab = 'all';
	isJobSave: boolean = false;
	isJobRemove: boolean = false;
	userPreferences: any = {};



	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private studentJobService: StudentJobService,
		private toastr: ToastrService,
		private authService: AuthServiceService
	) { }


	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			if (params['tab']) {
				this.selectedTab = params['tab'];
			}
		});

		this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()
		).subscribe(value => {
			this.searchTerm = value;
			this.currentPage = 1;
			this.jobData = [];
			this.getAllStudentsJobs();
		});


		this.locationSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
			this.location = value;
			this.currentPage = 1;
			this.jobData = [];
			this.getAllStudentsJobs();
		});

		this.authService.getUserDetails(localStorage.getItem('user_id') || '').subscribe(res => {
			if (res.Success && res.Data) {
				this.userPreferences = {
					preferred_job_role_category: res.Data.preferred_job_role_category,
					preferred_job_title: res.Data.preferred_job_title,
					skills: res.Data.skills,
					resume_url: res.Data.resume_url
				};
			}
			this.getAllStudentsJobs();
		});
	}


	getAllStudentsJobs() {
		this.isLoading = true;

		const isProfileComplete = !!(
			this.userPreferences.preferred_job_role_category &&
			this.userPreferences.preferred_job_title &&
			this.userPreferences.skills &&
			this.userPreferences.skills.length > 0 &&
			this.userPreferences.resume_url
		);

		const actualTab = (this.selectedTab === 'recommended' && !isProfileComplete) ? 'all' : this.selectedTab;

		const filters: any = {
			job_type: this.selectedJobType,
			work_mode: this.selectedWorkMode,
			location: this.location,
			is_visible: true
		};
		if (this.selectedTab === 'recommended' && isProfileComplete) {
			if (this.userPreferences.preferred_job_role_category) filters.preferred_job_role_category = this.userPreferences.preferred_job_role_category;
			if (this.userPreferences.preferred_job_title) filters.preferred_job_title = this.userPreferences.preferred_job_title;
			if (this.userPreferences.skills && this.userPreferences.skills.length > 0) filters.skills = this.userPreferences.skills;
		}

		this.subscription.add(
			this.studentJobService.getAllStudentsJobs(
				this.currentPage,
				this.limit,
				actualTab,
				this.selectedStatus,
				this.searchTerm,
				filters
			).pipe(
				tap(response => {
					if (response.Success) {
						let jobs = (response.Data.jobs || []).map(job => ({
							...job,
							locations: (job.locations || []).filter(location => location?.trim())
						}));

						if (this.selectedTab === 'recommended' && !isProfileComplete) {
							jobs = [];
						}

						if (this.currentPage === 1) {
							this.jobData = jobs;
						} else {
							this.jobData = [...this.jobData, ...jobs];
						}
						
						if (this.selectedTab === 'recommended') {
							this.recommendedCount = isProfileComplete ? response.Data.total : 0;
						} else {
							this.allJobsCount = response.Data.total;
						}
						
						if (response.Data.recommended_count !== undefined) {
							this.recommendedCount = isProfileComplete ? response.Data.recommended_count : 0;
						}
						if (response.Data.my_applications_count !== undefined) {
							this.myApplicationsCount = response.Data.my_applications_count;
						}
						if (response.Data.saved_count !== undefined) {
							this.savedCount = response.Data.saved_count;
						}
						
						this.total = this.selectedTab === 'recommended' && !isProfileComplete ? 0 : response.Data.total;
						this.pages = response.Data.page;
						this.limit = response.Data.limit;
						this.totalPages = this.selectedTab === 'recommended' && !isProfileComplete ? 0 : response.Data.total_pages;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}



	goToJobsDetailPage(jobId: string) {
		this.router.navigate(['/student-dashboard/jobs-detail-page', jobId]);
	}


	onSearch(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		this.searchSubject.next(value);
	}


	onLocationSearch(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		this.locationSubject.next(value);
	}


	onJobTypeChange(event: Event) {
		this.selectedJobType = (event.target as HTMLSelectElement).value;
		this.currentPage = 1;
		this.getAllStudentsJobs();
	}


	onWorkModeChange(mode: string) {
		this.selectedWorkMode = mode === 'All' ? '' : mode;
		this.currentPage = 1;
		this.jobData = [];
		this.getAllStudentsJobs();
	}


	loadMore() {
		if (this.currentPage < this.totalPages && !this.isLoading) {
			this.currentPage++;
			this.getAllStudentsJobs();
		}
	}

	changeTab(tab: string) {
		this.selectedTab = tab;
		this.currentPage = 1;
		this.jobData = [];
		this.getAllStudentsJobs();
	}


	truncateText(text: string, limit: number): string {
		return text?.length > limit ? text.slice(0, limit) + '...' : text;
	}


	toggleSaveJob(job: GetAllStudentJobs) {
		if (job.is_saved) {
			this.deleteStudentSaveJob(job);
		} else {
			this.postStudentSaveJob(job);
		}
	}


	postStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobSave = true;

		this.subscription.add(
			this.studentJobService.postStudentSaveJob(job.job_id).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Data.message);
						this.getAllStudentsJobs();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobSave = false;
				})
			).subscribe()
		);
	}


	deleteStudentSaveJob(job: GetAllStudentJobs) {
		this.isJobRemove = true;

		this.subscription.add(
			this.studentJobService.deleteStudentSaveJob(job.job_id).pipe(
				tap(response => {
					if (response.Success) {
						this.toastr.success(response.Data.message);
						this.getAllStudentsJobs();
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isJobRemove = false;
				})
			).subscribe()
		)
	}

	getDaysAgo(dateString: string | Date | undefined | null): string | null {
		if (!dateString) return null;
		
		const publishedDate = new Date(dateString);
		const today = new Date();
		
		const timeDiff = today.getTime() - publishedDate.getTime();
		const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
		
		if (daysDiff >= 0 && daysDiff <= 30) {
			if (daysDiff === 0) return 'Posted today';
			if (daysDiff === 1) return 'Posted 1 day ago';
			return `Posted ${daysDiff} days ago`;
		}
		
		return null;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}
